import * as React from 'react';
import { cn } from '@/lib/utils';
import { Button, Paragraph } from '@/components/atoms';
import { MessageBubble } from '@/components/atoms';
import { ChatComposer } from '@/components/molecules/chat-composer';
import { TemplateSendSheet } from '@/pages/dashboard/conversations/components/template-send-sheet';
import { formatChatHeaderDate } from '@/lib/helpers';
import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ClockIcon,
  RefreshCwIcon,
  SparklesIcon,
  XIcon,
} from 'lucide-react';
import type { ChannelType, ChatOutboundPayload, Message } from '@/types';

/** Set to `true` to show the composer “Respond with AI” control again. */
const ENABLE_RESPOND_WITH_AI = false;

const SCROLL_NEAR_BOTTOM_PX = 80;

export type { Message };

export interface ChatInterfaceProps {
  selectedConversation?: {
    id: string;
    name: string;
    initials: string;
    responseWindow?: {
      expiresAt: string | null;
      extendedWindowExpiresAt: string | null;
      isExpired: boolean;
      canSendFreeForm: boolean;
    };
  };
  messages: Message[];
  onSendMessage?: (payload: ChatOutboundPayload) => void;
  onRetryMessage?: (messageId: string) => void;
  onReactToMessage?: (messageId: string, emoji: string) => void;
  composerDisabled?: boolean;
  isChannelConnected?: boolean;
  currentChannel?: ChannelType;
  onConnectChannel?: (channel: ChannelType) => void;
  /** When provided, back control is shown on small screens (CSS) to return to list */
  onBack?: () => void;
  /** Prefill composer when `key` changes (e.g. after AI suggestion). */
  composerPrefill?: { text: string; key: number } | null;
  onRespondWithAi?: () => void;
  isRespondWithAiLoading?: boolean;
  /** True after requesting AI, until `ai_response` arrives via websocket. */
  isAiDraftPending?: boolean;
  /** Allow "Respond with AI" when there is at least one inbound message in the thread. */
  canRespondWithAi?: boolean;
  /** Shown when a new message may have arrived but the thread did not update. */
  showIncomingMessageNotice?: boolean;
  isRefreshingIncomingMessages?: boolean;
  onRefreshIncomingMessages?: () => void;
  onDismissIncomingMessageNotice?: () => void;
  showTemplates?: boolean;
  onSendTemplate?: (payload: {
    templateId: number;
    templateTitle: string;
    previewBody: string;
    templateVariables: Record<string, string>;
    variableNames: string[];
  }) => void;
  isSendingTemplate?: boolean;
  className?: string;
}

const formatRemaining = (ms: number) => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

function isNearBottom(container: HTMLElement) {
  return (
    container.scrollHeight - container.scrollTop - container.clientHeight <
    SCROLL_NEAR_BOTTOM_PX
  );
}

function ChatInterface({
  messages,
  onSendMessage,
  onRetryMessage,
  onReactToMessage,
  composerDisabled = false,
  isChannelConnected = true,
  currentChannel,
  onConnectChannel,
  onBack,
  composerPrefill,
  onRespondWithAi,
  isRespondWithAiLoading,
  isAiDraftPending,
  canRespondWithAi,
  showIncomingMessageNotice = false,
  isRefreshingIncomingMessages = false,
  onRefreshIncomingMessages,
  onDismissIncomingMessageNotice,
  showTemplates = false,
  onSendTemplate,
  isSendingTemplate = false,
  className,
  ...props
}: ChatInterfaceProps) {
  const [templateSheetOpen, setTemplateSheetOpen] = React.useState(false);
  const [visibleDate, setVisibleDate] = React.useState<string>('');
  const [showJumpToLatest, setShowJumpToLatest] = React.useState(false);
  const [now, setNow] = React.useState(() => Date.now());
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const messageRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const shouldStickToBottomRef = React.useRef(true);
  const scrollConversationIdRef = React.useRef<string | undefined>(undefined);
  const previousMessageCountRef = React.useRef(0);
  const firstMessageIdRef = React.useRef<string | undefined>(undefined);

  const responseWindow = props.selectedConversation?.responseWindow;
  const conversationId = props.selectedConversation?.id;
  const freeFormDisabled = Boolean(
    responseWindow &&
      (responseWindow.isExpired || responseWindow.canSendFreeForm === false)
  );

  React.useEffect(() => {
    if (
      !responseWindow?.expiresAt ||
      responseWindow.isExpired ||
      !responseWindow.canSendFreeForm
    ) {
      return;
    }
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [
    responseWindow?.expiresAt,
    responseWindow?.isExpired,
    responseWindow?.canSendFreeForm,
  ]);

  const headerTimer = React.useMemo(() => {
    const rw = props.selectedConversation?.responseWindow;
    if (!rw) return null;
    if (!rw.canSendFreeForm || rw.isExpired) {
      return { label: 'Expired', tone: 'muted' as const };
    }
    if (!rw.expiresAt) return null;
    const expiresAtMs = new Date(rw.expiresAt).getTime();
    const remaining = expiresAtMs - now;
    if (remaining <= 0) return { label: 'Expired', tone: 'muted' as const };
    return { label: formatRemaining(remaining), tone: 'active' as const };
  }, [now, props.selectedConversation?.responseWindow]);

  const scrollToBottom = React.useCallback(
    (behavior: ScrollBehavior = 'auto') => {
      const container = scrollRef.current;
      if (!container) return;
      container.scrollTo({ top: container.scrollHeight, behavior });
      shouldStickToBottomRef.current = true;
      setShowJumpToLatest(false);
    },
    []
  );

  React.useEffect(() => {
    messageRefs.current.length = messages.length;
  }, [messages.length]);

  React.useEffect(() => {
    if (messages.length > 0) {
      const last = messages[messages.length - 1];
      setVisibleDate(formatChatHeaderDate(last?.createdAt) || '');
    } else {
      setVisibleDate('');
    }
  }, [messages]);

  React.useEffect(() => {
    const container = scrollRef.current;
    if (!container || messages.length === 0) return;

    const conversationChanged =
      conversationId !== scrollConversationIdRef.current;
    const firstMessageId = messages[0]?.id;
    const messagesPrepended =
      !conversationChanged &&
      firstMessageIdRef.current != null &&
      firstMessageId !== firstMessageIdRef.current;
    firstMessageIdRef.current = firstMessageId;

    if (conversationChanged) {
      scrollConversationIdRef.current = conversationId;
      previousMessageCountRef.current = 0;
      firstMessageIdRef.current = firstMessageId;
      shouldStickToBottomRef.current = true;
      setShowJumpToLatest(false);
    }

    const previousScrollHeight = container.scrollHeight;
    const previousScrollTop = container.scrollTop;
    const messageCountIncreased =
      messages.length > previousMessageCountRef.current;
    previousMessageCountRef.current = messages.length;

    if (conversationChanged || shouldStickToBottomRef.current) {
      requestAnimationFrame(() => scrollToBottom('auto'));
      return;
    }

    if (messagesPrepended && messageCountIncreased) {
      requestAnimationFrame(() => {
        if (!scrollRef.current) return;
        const heightDelta =
          scrollRef.current.scrollHeight - previousScrollHeight;
        scrollRef.current.scrollTop = previousScrollTop + heightDelta;
      });
      return;
    }

    if (messageCountIncreased && isNearBottom(container)) {
      requestAnimationFrame(() => scrollToBottom('smooth'));
    }
  }, [messages, conversationId, scrollToBottom]);

  const handleScroll = React.useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    const nearBottom = isNearBottom(container);
    shouldStickToBottomRef.current = nearBottom;
    setShowJumpToLatest(!nearBottom && messages.length > 0);

    if (messages.length === 0) return;

    const scrollTop = container.scrollTop;
    const headerOffset = 60;
    for (let i = messageRefs.current.length - 1; i >= 0; i--) {
      const el = messageRefs.current[i];
      if (!el) continue;
      const top = el.offsetTop;
      const height = el.offsetHeight;
      if (top <= scrollTop + headerOffset && top + height >= scrollTop) {
        const createdAt = messages[i]?.createdAt;
        if (createdAt) setVisibleDate(formatChatHeaderDate(createdAt));
        return;
      }
    }
    const first = messages[0]?.createdAt;
    if (first) setVisibleDate(formatChatHeaderDate(first));
  }, [messages]);

  // Show empty state if channel is not connected
  if (!isChannelConnected && currentChannel) {
    return (
      <div
        className={cn(
          'flex-1 flex flex-col bg-white px-2 lg:px-6 py-4 rounded-md items-center justify-center',
          className
        )}
        {...props}
      >
        <div className="text-center max-w-md">
          <div className="text-6xl mb-6">😔</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            No {currentChannel} conversations yet
          </h2>
          <Paragraph className="text-gray-500 mb-6">
            Connect your {currentChannel} account to start receiving messages
          </Paragraph>
          {onConnectChannel && (
            <Button
              onClick={() => onConnectChannel(currentChannel)}
              className="px-6 py-3 bg-primary text-white rounded-md hover:bg-primary/90"
            >
              Connect {currentChannel}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex min-h-0 h-full max-h-[90vh] flex-1 flex-col overflow-hidden bg-white py-4 lg:px-6 rounded-md',
        className
      )}
      {...props}
    >
      {/* Chat Header: back + name on small screens (when onBack provided), date on lg */}
      <div className="flex-shrink-0 border-b border-gray-100 bg-white p-4">
        {onBack && (
          <div className="flex items-center gap-2 max-lg:flex lg:hidden">
            <Button
              variant="ghost"
              size="sm"
              className="rounded-full p-2 -ml-2"
              onClick={onBack}
              aria-label="Back to conversations"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </Button>
            <Paragraph size="sm" className="font-medium truncate flex-1">
              {props.selectedConversation?.name ?? 'Chat'}
            </Paragraph>
          </div>
        )}
        <div
          className={cn(
            'flex items-center justify-center gap-3',
            onBack && 'max-lg:hidden lg:flex'
          )}
        >
          <p className="text-base text-black">
            {visibleDate || (messages.length > 0 ? 'Today' : '')}
          </p>
          {headerTimer && (
            <div
              className={cn(
                'flex items-center gap-1 text-sm',
                headerTimer.tone === 'active'
                  ? 'text-green-600'
                  : 'text-red-500'
              )}
              title={
                headerTimer.label === 'Expired'
                  ? 'Response window expired'
                  : 'Response window remaining'
              }
            >
              <ClockIcon className="w-4 h-4" />
              <span className="tabular-nums">{headerTimer.label}</span>
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="relative min-h-0 flex-1">
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="h-full min-h-0 space-y-1 overflow-y-auto overflow-x-hidden px-4 py-4 lg:px-6"
        >
          {messages.length === 0 && !props.selectedConversation ? (
            <div className="flex h-full items-center justify-center">
              <Paragraph className="text-gray-400">
                Select a conversation to start chatting
              </Paragraph>
            </div>
          ) : (
            messages.map((message, index) => {
              const prevMessage = index > 0 ? messages[index - 1] : null;
              const nextMessage =
                index < messages.length - 1 ? messages[index + 1] : null;

              const isFirstInGroup =
                !prevMessage || prevMessage.sender !== message.sender;
              const isLastInGroup =
                !nextMessage || nextMessage.sender !== message.sender;
              const showAvatar = isFirstInGroup;

              return (
                <div
                  key={message.id}
                  ref={(el) => {
                    messageRefs.current[index] = el;
                  }}
                >
                  <MessageBubble
                    {...message}
                    isFirstInGroup={isFirstInGroup}
                    isLastInGroup={isLastInGroup}
                    showAvatar={showAvatar}
                    deliveryStatus={message.deliveryStatus}
                    mediaType={message.mediaType}
                    mediaUrl={message.mediaUrl}
                    fileName={message.fileName}
                    mimeType={message.mimeType}
                    fileSize={message.fileSize}
                    uploadProgress={message.uploadProgress}
                    reactions={message.reactions}
                    onReact={
                      onReactToMessage
                        ? (emoji) => onReactToMessage(message.id, emoji)
                        : undefined
                    }
                    onRetry={
                      message.deliveryStatus === 'failed' && onRetryMessage
                        ? () => onRetryMessage(message.id)
                        : undefined
                    }
                  />
                </div>
              );
            })
          )}
        </div>

        {showJumpToLatest && (
          <div className="pointer-events-none absolute bottom-3 right-3 z-20 flex justify-end">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => scrollToBottom('smooth')}
              className="pointer-events-auto h-9 gap-1.5 rounded-full border-gray-200 bg-white px-3 text-gray-700 shadow-md hover:bg-gray-50"
              aria-label="Jump to latest messages"
            >
              <ArrowDownIcon className="size-4 shrink-0" />
              <span className="hidden text-xs font-semibold sm:inline">
                Latest messages
              </span>
            </Button>
          </div>
        )}
      </div>

      {showIncomingMessageNotice && (
        <div className="relative z-10 flex shrink-0 justify-center px-4 pb-2 pt-1">
          <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-[#C8F3D2] bg-[#EAFFED] px-2 py-1.5 pl-3 text-[#18854F] shadow-sm">
            <span className="truncate text-xs font-semibold">
              New message received
            </span>
            {onRefreshIncomingMessages && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={isRefreshingIncomingMessages}
                onClick={onRefreshIncomingMessages}
                className="h-7 shrink-0 rounded-full bg-white px-2.5 text-xs font-semibold text-[#18854F] hover:bg-[#DCF5E1]"
              >
                <RefreshCwIcon
                  className={cn(
                    'mr-1 size-3.5',
                    isRefreshingIncomingMessages && 'animate-spin'
                  )}
                />
                {isRefreshingIncomingMessages ? 'Refreshing…' : 'Refresh'}
              </Button>
            )}
            {onDismissIncomingMessageNotice && (
              <button
                type="button"
                onClick={onDismissIncomingMessageNotice}
                className="flex size-6 shrink-0 items-center justify-center rounded-full text-[#18854F]/70 transition-colors hover:bg-white hover:text-[#18854F]"
                aria-label="Dismiss new message notice"
              >
                <XIcon className="size-3.5" />
              </button>
            )}
          </div>
        </div>
      )}

      <div className="shrink-0 space-y-2 bg-white p-4 lg:bg-gray-100">
        {freeFormDisabled && showTemplates && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-950 sm:text-sm">
            <p className="font-semibold">
              24-hour customer service window has expired
            </p>
            <p className="mt-0.5 text-amber-900/80">
              To stay compliant with WhatsApp policy, send an approved template
              instead of a free-form message.
            </p>
          </div>
        )}
        {ENABLE_RESPOND_WITH_AI && onRespondWithAi && (
          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isRespondWithAiLoading || canRespondWithAi === false}
              onClick={onRespondWithAi}
              className="rounded-full border-none bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:from-purple-600 hover:to-blue-600 disabled:opacity-50"
              title={
                canRespondWithAi === false
                  ? 'Receive a customer message first to get an AI reply draft'
                  : 'Draft a reply with AI'
              }
            >
              {isRespondWithAiLoading || isAiDraftPending ? (
                <ClockIcon className="mr-1.5 h-3.5 w-3.5" />
              ) : (
                <SparklesIcon className="mr-1.5 h-3.5 w-3.5" />
              )}
              {isRespondWithAiLoading
                ? 'Requesting…'
                : isAiDraftPending
                  ? 'Drafting…'
                  : 'Respond with AI'}
            </Button>
          </div>
        )}
        {onSendMessage && (
          <ChatComposer
            onSend={onSendMessage}
            disabled={composerDisabled}
            freeFormDisabled={freeFormDisabled}
            showTemplates={showTemplates}
            onOpenTemplates={() => setTemplateSheetOpen(true)}
            prefill={composerPrefill}
          />
        )}
      </div>

      {showTemplates && onSendTemplate && (
        <TemplateSendSheet
          open={templateSheetOpen}
          onOpenChange={setTemplateSheetOpen}
          conversationName={props.selectedConversation?.name}
          isSending={isSendingTemplate}
          onSend={(payload) => {
            onSendTemplate(payload);
            setTemplateSheetOpen(false);
          }}
        />
      )}
    </div>
  );
}

export { ChatInterface };
